import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { IApplyInfoData, IRowData, ITableResponse } from "@/src/types/applicationOnline";
import { onlineApplicationsDefaultConfiguration, onlineApplicationAllFieldsList } from "@/src/constants/applicationOnlineConsts";
import { getUserConfigFromStorage } from "@/src/utils/userConfigStorage";

interface ColumnConfig {
  title: string;
  id: string;
  width?: number;
}

/** Migrates saved table config: rename legacy column ids only. */
function normalizeVisibleAOPColumns(columns: ColumnConfig[]): ColumnConfig[] {
  return columns.map((c) => {
    if (c.id === "id") return { ...c, title: "Applicant ID" };
    if (c.id === "student_id") return { ...c, id: "applicant_id", title: "ID No" };
    return c;
  });
}

interface ApplicationsTableState {
  visibleColumns: ColumnConfig[];
  hiddenColumns: ColumnConfig[];
  data: IApplyInfoData[];
  selectedRows: number[];
}

const userConfig = getUserConfigFromStorage();
const initialVisibleColumns = normalizeVisibleAOPColumns(
  userConfig?.AOPConfig?.AOPTable ?? onlineApplicationsDefaultConfiguration,
);

const initialState: ApplicationsTableState = {
  visibleColumns: initialVisibleColumns,
  hiddenColumns: onlineApplicationAllFieldsList.filter(
    (col) => !initialVisibleColumns.some((visibleCol) => visibleCol.id === col.id),
  ),
  data: [],
  selectedRows: [],
};

const applicationsTableSlice = createSlice({
  name: "applicationsTable",
  initialState,
  reducers: {
    setData(state, action: PayloadAction<ITableResponse>) {
      state.data = action.payload.data;
    },
    toggleColumnVisibility(state, action: PayloadAction<string>) {
      const colId = action.payload;
      const isVisible = state.visibleColumns.some((col) => col.id === colId);
      if (isVisible) {
        const col = state.visibleColumns.find((col) => col.id === colId)!;
        state.visibleColumns = state.visibleColumns.filter((col) => col.id !== colId);
        state.hiddenColumns.push({ title: col.title, id: col.id, width: col.width });
      } else {
        let col = state.hiddenColumns.find((col) => col.id === colId);
        if (!col) {
          col = onlineApplicationAllFieldsList.find((c) => c.id === colId)!;
        }
        state.hiddenColumns = state.hiddenColumns.filter((c) => c.id !== colId);
        state.visibleColumns.push({ title: col.title, id: col.id, width: col.width ?? 4 });
      }
    },
    setColumnWidth(state, action: PayloadAction<{ id: string; width: number }>) {
      const { id, width } = action.payload;
      const col = state.visibleColumns.find((c) => c.id === id);
      if (col) col.width = width;
    },
    updateRow(state, action: PayloadAction<IRowData>) {
      const updatedRow = action.payload;
      const index = state.data.findIndex((row) => row.id === updatedRow.applyInfo.id);
      if (index !== -1) {
        state.data[index] = {
          ...state.data[index],
          ...updatedRow.applyInfo,
          fullname: updatedRow.userInfo.fullname,
          email: updatedRow.userInfo.email,
          phone: updatedRow.userInfo.phone,
          country: updatedRow.userInfo.country,
          nationality: updatedRow.userInfo.nationality,
          dateOfBirth: updatedRow.userInfo.dateOfBirth,
          address: updatedRow.userInfo.address,
          passportNo: updatedRow.userInfo.passportNo,
          passportIssued: updatedRow.userInfo.passportIssued,
          validPassport: updatedRow.userInfo.validPassport,
        };
      }
    },
    toggleRowSelection(state, action: PayloadAction<number>) {
      const id = action.payload;
      if (state.selectedRows.includes(id)) {
        state.selectedRows = state.selectedRows.filter((rowId) => rowId !== id);
      } else {
        state.selectedRows.push(id);
      }
    },
    toggleAllRows(state) {
      if (state.selectedRows.length === state.data.length) {
        state.selectedRows = [];
      } else {
        state.selectedRows = state.data.map((row) => row.id);
      }
    },
    setActualFieldValue(state, action: PayloadAction<{ id: number; fieldActualValue: string; keyField: string }>) {
      const { id, fieldActualValue, keyField } = action.payload;
      const row = state.data.find((r) => r.id === id);
      if (!row) return;

      switch (keyField) {
        case "color_star":
          row.color_star = fieldActualValue;
          console.log(`Star color changed for ${id}: ${fieldActualValue}`);
          break;

        case "form_status":
          row.form_status = fieldActualValue;
          console.log(`Status changed for ${id}: ${fieldActualValue}`);
          break;

        default:
          console.warn(`Unknown keyField: ${keyField}`);
          break;
      }
    },
    showAllColumns(state) {
      state.visibleColumns = [...onlineApplicationAllFieldsList];
      state.hiddenColumns = [];
    },

    hideAllColumns(state) {
      state.hiddenColumns = [...onlineApplicationAllFieldsList];
      state.visibleColumns = [];
    },
    setVisibleColumns(state, action: PayloadAction<ColumnConfig[]>) {
      const normalized = normalizeVisibleAOPColumns(action.payload);
      state.visibleColumns = normalized;
      state.hiddenColumns = onlineApplicationAllFieldsList.filter((col) => !normalized.some((visibleCol) => visibleCol.id === col.id));
    },
  },
});

export const {
  setData,
  toggleColumnVisibility,
  setColumnWidth,
  toggleRowSelection,
  toggleAllRows,
  setActualFieldValue,
  updateRow,
  // setStatus,
  showAllColumns,
  hideAllColumns,
  setVisibleColumns,
} = applicationsTableSlice.actions;

export default applicationsTableSlice.reducer;
